/**
 * Typed client for this server's /api/v1/incidents.
 *
 * Production (app.neuravue.com) calls the Express routes on this app.
 * Development keeps fixture records. Force either mode with
 *   REACT_APP_INCIDENTS_USE_FIXTURES=true|false
 *   REACT_APP_INCIDENTS_API_BASE=https://host/api/v1/incidents
 *
 * A camera alert does not create a record. Staff confirm does:
 * POST /incidents/from-alert/:alertId/confirm inserts pending_review and opens the form.
 * Confirm does not submit. Clocks use confirmed_at.
 */
import { apiURL } from "config/constants";
import { buildExportHtml } from "./exportDocument";
import { fixtureApi, IncidentApiError, monthKey, resetIncidentFixtures, visibleFacilities } from "./fixtureStore";
import {
  AlertIncidentSeed,
  DismissalReport,
  DocumentationSave,
  ExportPayload,
  FalseAlertInput,
  Incident,
  IncidentActor,
  IncidentAmendment,
  IncidentListQuery,
  VerifyResult
} from "./types";

export { IncidentApiError, monthKey, resetIncidentFixtures, visibleFacilities };

export function incidentsApiBase(): string {
  const configured = process.env.REACT_APP_INCIDENTS_API_BASE;
  if (configured) return configured.replace(/\/$/, "");
  return `${apiURL}/incidents`;
}

export function incidentsUseFixtures(): boolean {
  if (process.env.REACT_APP_INCIDENTS_USE_FIXTURES === "true") return true;
  if (process.env.REACT_APP_INCIDENTS_USE_FIXTURES === "false") return false;
  return process.env.NODE_ENV !== "production";
}

async function liveRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${incidentsApiBase()}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {})
    },
    ...init
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    throw new IncidentApiError(
      json?.message || json?.error || "Incident request failed.",
      response.status,
      json?.errors || json?.data?.errors,
      json?.msgCode
    );
  }
  return (json?.data ?? json) as T;
}

function asIncident(payload: unknown): Incident {
  const body = payload as { incident?: Incident };
  const incident = body.incident || (payload as Incident);
  return {
    ...incident,
    documentation: incident.documentation ?? {},
    notifications: incident.notifications ?? [],
    reasonable_grounds_decision: incident.reasonable_grounds_decision ?? { decision: null },
    events: incident.events ?? [],
    amendments: incident.amendments ?? []
  };
}

function asList(payload: unknown): Incident[] {
  if (Array.isArray(payload)) return payload as Incident[];
  const body = payload as { incidents?: Incident[]; items?: Incident[] };
  return body.incidents || body.items || [];
}

const liveApi = {
  list(query: IncidentListQuery = {}): Promise<Incident[]> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const qs = params.toString();
    return liveRequest<unknown>(qs ? `?${qs}` : "").then(asList);
  },
  get(id: string): Promise<Incident> {
    return liveRequest<unknown>(`/${id}`).then(asIncident);
  },
  fromAlert(alertId: string): Promise<Incident> {
    return liveRequest<unknown>(`/from-alert/${encodeURIComponent(alertId)}`, {
      method: "POST",
      body: JSON.stringify({})
    }).then(asIncident);
  },
  confirmFromAlert(
    alertId: string,
    input: { reconfirm?: boolean; threatStatus?: string; seed?: AlertIncidentSeed; actor?: IncidentActor } = {}
  ): Promise<Incident> {
    return liveRequest<unknown>(`/from-alert/${encodeURIComponent(alertId)}/confirm`, {
      method: "POST",
      body: JSON.stringify({ reconfirm: input.reconfirm === true })
    }).then(asIncident);
  },
  confirm(id: string, actor: IncidentActor): Promise<Incident> {
    return liveRequest<unknown>(`/${id}/confirm`, {
      method: "POST",
      body: JSON.stringify({ confirmed_by: actor.user_id, name: actor.name })
    }).then(asIncident);
  },
  saveDocumentation(id: string, save: DocumentationSave): Promise<Incident> {
    return liveRequest<unknown>(`/${id}/documentation`, {
      method: "PUT",
      body: JSON.stringify({
        documentation: save.documentation,
        notifications: save.notifications,
        reasonable_grounds_decision: save.reasonable_grounds_decision,
        province: save.province,
        province_confirmed: save.province_confirmed,
        bc_template: save.bc_template
      })
    }).then(asIncident);
  },
  submit(id: string, actor: IncidentActor): Promise<Incident> {
    return liveRequest<unknown>(`/${id}/submit`, {
      method: "POST",
      body: JSON.stringify({
        attestation_name: actor.name,
        attestation_designation: actor.designation,
        user_id: actor.user_id
      })
    }).then(asIncident);
  },
  amend(
    id: string,
    input: { reason: string; changes: IncidentAmendment["changes"]; actor: IncidentActor }
  ): Promise<Incident> {
    return liveRequest<unknown>(`/${id}/amendments`, {
      method: "POST",
      body: JSON.stringify({
        reason: input.reason,
        changes: input.changes,
        user_id: input.actor.user_id,
        name: input.actor.name
      })
    }).then(asIncident);
  },
  async falseAlert(id: string, input: FalseAlertInput) {
    const payload = await liveRequest<{ incident?: Incident; converted?: boolean; message?: string }>(
      `/${id}/false-alert`,
      {
        method: "POST",
        body: JSON.stringify({
          reason_code: input.reason_code,
          note: input.note,
          risk_situation: input.risk_situation,
          concern: input.concern
        })
      }
    );
    const incident = asIncident(payload);
    const converted =
      typeof payload?.converted === "boolean"
        ? payload.converted
        : Boolean(incident.confirmed_at) && incident.status === "in_documentation";
    return {
      incident,
      converted,
      message: payload?.message || (converted ? "This stays an incident." : "Dismissal recorded.")
    };
  },
  confirmFalseAlert(id: string, input: { decision: "confirm" | "reject"; note?: string; actor: IncidentActor }) {
    return liveRequest<unknown>(`/${id}/false-alert/confirm`, {
      method: "POST",
      body: JSON.stringify({
        decision: input.decision,
        note: input.note,
        user_id: input.actor.user_id,
        name: input.actor.name
      })
    }).then(asIncident);
  },
  escalate(id: string, actor: IncidentActor, note: string) {
    return liveRequest<unknown>(`/${id}/escalate`, {
      method: "POST",
      body: JSON.stringify({ user_id: actor.user_id, name: actor.name, note })
    }).then(asIncident);
  },
  review(id: string, actor: IncidentActor, note: string, designation: string) {
    return liveRequest<unknown>(`/${id}/signoff`, {
      method: "POST",
      body: JSON.stringify({ note, designation: designation || actor.designation })
    }).then(asIncident);
  },
  close(id: string, actor: IncidentActor) {
    return liveRequest<unknown>(`/${id}/close`, {
      method: "POST",
      body: JSON.stringify({ user_id: actor.user_id, name: actor.name })
    }).then(asIncident);
  },
  async export(id: string, format?: "html" | "bc" | "pdf"): Promise<ExportPayload> {
    if (format === "bc" || format === "pdf") {
      const response = await fetch(`${incidentsApiBase()}/${id}/export?format=${format}`, {
        credentials: "include",
        headers: { Accept: "text/html" }
      });
      const html = await response.text();
      if (!response.ok) {
        let message = "Incident request failed.";
        try {
          message = JSON.parse(html)?.message || message;
        } catch {
          message = html.slice(0, 180) || message;
        }
        throw new IncidentApiError(message, response.status);
      }
      const incident = asIncident(await liveRequest<unknown>(`/${id}`));
      return { incident, html };
    }
    const payload = await liveRequest<ExportPayload & { html?: string }>(`/${id}/export`);
    const incident = payload.incident || asIncident(payload);
    return { incident, html: payload.html || buildExportHtml(incident) };
  },
  verify(id: string): Promise<VerifyResult> {
    return liveRequest<VerifyResult>(`/${id}/verify`).then((result) => ({
      valid: Boolean(result.valid),
      hash: result.hash,
      algorithm: "server-sha256" as const
    }));
  },
  dismissalReport(month: string, facility?: string): Promise<DismissalReport> {
    const params = new URLSearchParams({ month });
    if (facility && facility !== "all") params.set("facility", facility);
    return liveRequest<DismissalReport>(`/reports/dismissals?${params.toString()}`);
  }
};

export type IncidentsClient = typeof fixtureApi;

function client(): IncidentsClient {
  return incidentsUseFixtures() ? fixtureApi : (liveApi as IncidentsClient);
}

export const incidentsApi: IncidentsClient = {
  list: (query) => client().list(query),
  get: (id) => client().get(id),
  fromAlert: (alertId) => client().fromAlert(alertId),
  confirmFromAlert: (alertId, input) => client().confirmFromAlert(alertId, input),
  confirm: (id, actor) => client().confirm(id, actor),
  saveDocumentation: (id, save) => client().saveDocumentation(id, save),
  submit: (id, actor) => client().submit(id, actor),
  amend: (id, input) => client().amend(id, input),
  falseAlert: (id, input) => client().falseAlert(id, input),
  confirmFalseAlert: (id, input) => client().confirmFalseAlert(id, input),
  escalate: (id, actor, note) => client().escalate(id, actor, note),
  review: (id, actor, note, designation) => client().review(id, actor, note, designation),
  close: (id, actor) => client().close(id, actor),
  export: (id, format) => client().export(id, format),
  verify: (id) => client().verify(id),
  dismissalReport: (month, facility) => client().dismissalReport(month, facility)
};
